import { useState } from "react";
import { Alert, Button, Form, InputGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

export const INVALID_ID_MESSAGE = "Enter a positive whole-number rebalance id.";

/**
 * Validate a raw rebalance-id input. Returns the positive integer id, or
 * `null` when empty / non-numeric / non-positive / non-integer (R15 / AC14).
 */
export function parseRebalanceId(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "" || !/^\d+$/.test(trimmed)) return null;
  const id = Number(trimmed);
  if (!Number.isInteger(id) || id <= 0) return null;
  return id;
}

/**
 * Id-less surface at `/admin/rebalances/explain`: a manual rebalance-id input
 * (s-read-only-admin-explain-019ea1c7 R14 / R15). A positive integer navigates
 * to `/admin/rebalances/:rebalanceId/explain`; empty / non-numeric input shows
 * an inline validation message and does NOT navigate. The Spec-2 list deep-links
 * straight to the id-bearing path, bypassing this input.
 */
export default function AdminRebalanceExplainInput() {
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const id = parseRebalanceId(value);
    if (id == null) {
      setError(INVALID_ID_MESSAGE);
      return;
    }
    setError(null);
    navigate(`/admin/rebalances/${id}/explain`);
  };

  return (
    <section aria-label="Open cross-firm Explain">
      <h2 className="h5">Cross-firm Explain viewer</h2>
      <p className="text-muted">
        Enter a rebalance id to view its Explain for any firm.
      </p>
      <Form onSubmit={onSubmit} noValidate>
        <InputGroup>
          <Form.Control
            type="text"
            inputMode="numeric"
            aria-label="Rebalance id"
            placeholder="Rebalance id"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            isInvalid={error != null}
          />
          <Button type="submit" variant="primary">
            View Explain
          </Button>
        </InputGroup>
      </Form>
      {error != null && (
        <Alert variant="danger" role="alert" className="mt-2">
          {error}
        </Alert>
      )}
    </section>
  );
}
